import { CheckCircle, AlertCircle, XCircle } from 'lucide-react'
import { useState } from 'react'
import  { type ColumnMapping, type InventoryColumn, type MappingResult } from '#app/services/inventory-mapping.server'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'

const COLUMN_OPTIONS: InventoryColumn[] = [
  'Product',
  'On Hand Single', 
  'On Hand Cases',
  'Size',
  'Measure',
  'Size & Measure', // Add the combined option
  'Case Size',
  'Single Cost',
  'Case Cost',
  'Inventory Total'
]

const REQUIRED_COLUMNS: InventoryColumn[] = [
  'Product',
  'On Hand Single', 
  'On Hand Cases',
  'Size',
  'Measure',
  'Case Size',
  'Single Cost',
  'Case Cost',
  'Inventory Total'
]

interface InventoryMappingPreviewProps {
  csvData: any[]
  mappingResult: MappingResult
  onMappingChange: (mappings: ColumnMapping[]) => void
  onConfirm: () => void
  onCancel: () => void
}

export function InventoryMappingPreview({
  csvData,
  mappingResult,
  onMappingChange,
  onConfirm,
  onCancel
}: InventoryMappingPreviewProps) {
  const [mappings, setMappings] = useState<ColumnMapping[]>(mappingResult.mappings)

  const handleMappingChange = (sourceColumn: string, targetColumn: InventoryColumn | null) => {
    const newMappings = mappings.map(mapping => {
      if (mapping.sourceColumn === sourceColumn) {
        return {
          ...mapping,
          targetColumn,
          confidence: targetColumn ? 1 : 0
        }
      }
      // If this target is being assigned to a different source, clear it
      if (mapping.targetColumn === targetColumn && targetColumn !== null) {
        return {
          ...mapping,
          targetColumn: null,
          confidence: 0
        }
      }
      return mapping
    })
    
    setMappings(newMappings)
    onMappingChange(newMappings)
    
    // Update the hidden form input
    const mappingsInput = document.getElementById('mappingsInput') as HTMLInputElement
    if (mappingsInput) {
      mappingsInput.value = JSON.stringify(newMappings)
    }
  }

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600'
    if (confidence >= 0.5) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 0.8) return <CheckCircle className="w-4 h-4" />
    if (confidence >= 0.5) return <AlertCircle className="w-4 h-4" />
    return <XCircle className="w-4 h-4" />
  }

  const usedTargets = new Set(mappings.map(m => m.targetColumn).filter(Boolean))
  let requiredMissing = REQUIRED_COLUMNS.filter(col => !usedTargets.has(col))
  
  // If we have 'Size & Measure', we don't need separate 'Size' and 'Measure' columns
  if (usedTargets.has('Size & Measure')) {
    requiredMissing = requiredMissing.filter(col => col !== 'Size' && col !== 'Measure')
  }
  
  // If Size column contains combined data (and no separate Measure column), don't require Measure
  const sizeMapping = mappings.find(m => m.targetColumn === 'Size')
  if (sizeMapping && !usedTargets.has('Measure') && csvData.length > 0) {
    // Check if the Size column contains combined data like "3.5 GR"
    const sampleValue = csvData[0][sizeMapping.sourceColumn]
    if (sampleValue && /^(\d+(?:\.\d+)?)\s*([a-zA-Z#]+(?:\s+[a-zA-Z]+)*)$/.test(String(sampleValue))) {
      requiredMissing = requiredMissing.filter(col => col !== 'Measure')
    }
  }
  
  const canProcess = requiredMissing.length === 0

  // Show preview of first 5 rows
  const previewData = csvData.slice(0, 5)

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Map Your Spreadsheet Columns</CardTitle>
          <p className="text-sm text-gray-600">
            We've automatically detected and mapped your columns. Please review and adjust if needed.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {mappings.map((mapping) => (
              <div key={mapping.sourceColumn} className="flex items-center gap-4 p-3 border rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{mapping.sourceColumn}</div>
                  {csvData[0] && (
                    <div className="text-sm text-gray-500 truncate max-w-xs">
                      Example: {csvData[0][mapping.sourceColumn]}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {mapping.targetColumn && (
                    <div className={`flex items-center gap-1 ${getConfidenceColor(mapping.confidence)}`}>
                      {getConfidenceIcon(mapping.confidence)}
                      <span className="text-xs">
                        {Math.round(mapping.confidence * 100)}%
                      </span>
                    </div>
                  )}
                </div>

                <Select
                  value={mapping.targetColumn || "__NOT_MAPPED__"}
                  onValueChange={(value) => 
                    handleMappingChange(
                      mapping.sourceColumn, 
                      value === "__NOT_MAPPED__" ? null : value as InventoryColumn
                    )
                  }
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select mapping" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__NOT_MAPPED__">Not mapped</SelectItem>
                    {COLUMN_OPTIONS.map((option) => (
                      <SelectItem 
                        key={option} 
                        value={option}
                        disabled={usedTargets.has(option) && mapping.targetColumn !== option}
                      >
                        {option}
                        {usedTargets.has(option) && mapping.targetColumn !== option && (
                          <Badge variant="secondary" className="ml-2 text-xs">Used</Badge>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {requiredMissing.length > 0 && (
            <div className="mt-6 p-4 border border-red-200 rounded-lg bg-red-50">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                <AlertCircle className="w-4 h-4" />
                Missing Required Columns
              </div>
              <div className="text-sm text-red-600">
                The following columns are required but not mapped: {requiredMissing.join(', ')}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {previewData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Data Preview</CardTitle>
            <p className="text-sm text-gray-600">
              Here's how your data will look after mapping (showing first 5 rows):
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border">
                <thead>
                  <tr className="bg-gray-50">
                    {COLUMN_OPTIONS.map(col => (
                      <th key={col} className="p-2 text-left border font-medium">
                        {col}
                        {usedTargets.has(col) && (
                          <Badge variant="secondary" className="ml-1 text-xs">✓</Badge>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, index) => (
                    <tr key={index} className="border-b">
                      {COLUMN_OPTIONS.map(col => {
                        const mapping = mappings.find(m => m.targetColumn === col)
                        let value = mapping ? row[mapping.sourceColumn] : ''
                        
                        // Special handling for Size and Measure when we have Size & Measure
                        if (!mapping && usedTargets.has('Size & Measure')) {
                          const sizeAndMeasureMapping = mappings.find(m => m.targetColumn === 'Size & Measure')
                          if (sizeAndMeasureMapping) {
                            const combinedValue = row[sizeAndMeasureMapping.sourceColumn] || ''
                            if (col === 'Size') {
                              // Extract size part (number)
                              const match = String(combinedValue).match(/^(\d+(?:\.\d+)?)/)
                              value = match ? match[1] : combinedValue
                            } else if (col === 'Measure') {
                              // Extract measure part (letters)
                              const match = String(combinedValue).match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z#]+(?:\s+[a-zA-Z]+)*)$/)
                              value = match ? match[2] : ''
                            }
                          }
                        }
                        
                        // Handle case where combined data is mapped to Size column
                        if (mapping && mapping.targetColumn === 'Size' && value) {
                          const combinedValue = String(value)
                          const hasCombinedData = /^(\d+(?:\.\d+)?)\s*([a-zA-Z#]+(?:\s+[a-zA-Z]+)*)$/.test(combinedValue)
                          
                          if (hasCombinedData) {
                            if (col === 'Size') {
                              // Extract just the number part
                              const match = combinedValue.match(/^(\d+(?:\.\d+)?)/)
                              value = match ? match[1] : value
                            } else if (col === 'Measure') {
                              // Extract the measure part for the Measure column
                              const match = combinedValue.match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z#]+(?:\s+[a-zA-Z]+)*)$/)
                              value = match ? match[2] : ''
                            }
                          }
                        }
                        
                        return (
                          <td key={col} className="p-2 border">
                            {value || '-'}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          onClick={onConfirm}
          disabled={!canProcess}
          className={canProcess ? '' : 'opacity-50 cursor-not-allowed'}
        >
          {canProcess ? 'Process Inventory' : `Missing ${requiredMissing.length} Required Columns`}
        </Button>
      </div>
    </div>
  )
}